"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import BlackPill from "@/components/getting-started/BlackPill";

import { PagePhemexOfferSchema } from "@/sanity/pages/PagePhemexOfferTemplate";
import { PageBitunixOfferSchema } from "@/sanity/pages/PageBitunixOfferTemplate";

import griBGimg from "@/public/lp2/component1/grid-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function LearnSection({
  data,
  phemex,
  bitunix,
}: {
  data: PagePhemexOfferSchema | PageBitunixOfferSchema;
  phemex?: boolean;
  bitunix?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const content3Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const component2TL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      component2TL
        .from(content2Ref?.current?.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref?.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content3Ref?.current?.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.25,
          scrollTrigger: {
            trigger: content3Ref?.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden lg:mt-[4rem] learn-section-gradient lg:pb-[16rem]"
    >
      <div className="w-[min(193.5rem,100%)] px-[2rem] mx-auto relative">
        {/* Backgrounds */}
        <div
          ref={content1Ref}
          className="relative w-full mb-[-16rem] md:mb-[-20rem] lg:mb-[-20rem] xl:mb-[-28rem] 2xl:mb-[-22rem]"
        >
          <HLSVideoPlayer
            controls={false}
            playsInline={true}
            autoPlay={true}
            muted={true}
            loop={true}
            className="w-full h-[70vh] lg:h-auto !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
            source={{
              embed: false,
              muxVideo: {
                assetId: "",
                filename: "video-bg",
                status: "ready",
                playbackId: "1OfCrWJKYvirSBMsKn3UsIbmuEBPMoLsquPhlMcqOEs",
              },
              embedUrl: "",
              thumbnail: {
                src: "",
                width: 0,
                height: 0,
                lqip: "",
                name: "",
              },
            }}
          />

          {phemex && (
            <div className="absolute inset-0 w-full !max-w-[1920px] h-[99%] bg-[#37D201]/80 mix-blend-color z-[1] pointer-events-none" />
          )}

          {bitunix && (
            <div className="absolute inset-0 h-[99%] w-full bg-[rgba(93,247,200,1)] mix-blend-color z-[1] pointer-events-none" />
          )}

          <Image
            src={griBGimg}
            alt="Grid background"
            className="absolute z-[-3] left-0 right-0 bottom-0 w-full h-[50vh] lg:h-auto object-cover pointer-events-none"
          />
        </div>
        {/* End Backgrounds */}

        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[70%] flex flex-col items-center justify-center mx-auto mb-[4rem]"
          >
            {data?.learnTitles && (
              <PortableText
                value={data?.learnTitles ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill text={children as string} />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] lg:leading-[8.875rem] leading-[110%] text-center mt-[4rem] mb-[3rem] w-[min(105.75rem,100%)]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] lg:text-[2.25rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[min(110rem,100%)] mx-auto">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div
            ref={content3Ref}
            className="relative flex flex-col lg:flex-row-reverse items-center gap-[4rem]"
          >
            {data?.learnImage && (
              <GroqImage
                src={data?.learnImage}
                alt=""
                className="!w-[min(115.125rem,100%)] flex-shrink-0 lg:-my-[8rem]"
              />
            )}

            <div className="flex flex-col items-center lg:items-start text-center lg:text-start">
              {data?.learnTexts && (
                <PortableText
                  value={data?.learnTexts ?? []}
                  components={customRichTextComponent({
                    block: {
                      normal: ({ children }) => (
                        <p className="mb-[3rem] text-[2rem] lg:text-[2.25rem] font-[400] leading-[150%] text-white opacity-75">
                          {children}
                        </p>
                      ),
                    },
                  })}
                />
              )}

              <div className="mt-[1rem]">
                {data?.learnCTA && <CTAButton data={data?.learnCTA} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
